import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import Logo from '../../components/Logo';
import Footer from '../../components/Footer';
import ThemeToggle from '../../components/ThemeToggle';
import WalletMenu from '../../components/WalletMenu';

export default function HelicopterCharter() {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header - Minimalistic with no navigation menu */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <Logo />
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Helicopter Charter</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the ultimate in flexibility and convenience with our helicopter charter services. Perfect for short-distance travel and city-to-city transfers.
            </p>
          </div>

          {/* Features Section */}
          <div id="features" className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 scroll-mt-32">
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Safety First</h3>
              <p className="text-base text-gray-600">
                All helicopters in our fleet meet the highest safety standards with experienced pilots and rigorous maintenance.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Experienced pilots with 5,000+ flight hours</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Regular maintenance and safety checks</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Time Efficient</h3>
              <p className="text-base text-gray-600">
                Skip traffic and reduce travel time significantly with point-to-point helicopter transfers.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Direct routes without traffic constraints</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Access to helipads closer to your destination</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"></path>
                  <line x1="16" y1="8" x2="2" y2="22"></line>
                  <line x1="17.5" y1="15" x2="9" y2="15"></line>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Flexible Service</h3>
              <p className="text-base text-gray-600">
                Customized routes and schedules to meet your specific needs, with access to private helipads.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Personalized itineraries</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Access to exclusive landing sites</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pricing Section */}
          <div id="pricing" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold mb-8 text-center">Pricing Guide</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">Light Helicopters</h3>
                  <p className="text-base text-gray-600 mb-4">
                    Perfect for city transfers and short trips with 3-4 passengers.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Capacity</span>
                      <span className="font-medium">3-4 passengers</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Range</span>
                      <span className="font-medium">1-2 hour flights</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Price Range</span>
                      <span className="font-medium">5,000-7,000 €/h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Per Minute</span>
                      <span className="font-medium">30 €/min</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">Medium Helicopters</h3>
                  <p className="text-base text-gray-600 mb-4">
                    Versatile aircraft for longer trips and larger groups.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Capacity</span>
                      <span className="font-medium">5-8 passengers</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Range</span>
                      <span className="font-medium">2-3 hour flights</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Price Range</span>
                      <span className="font-medium">7,000-10,000 €/h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Per Minute</span>
                      <span className="font-medium">45 €/min</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">VIP Helicopters</h3>
                  <p className="text-base text-gray-600 mb-4">
                    Luxury helicopters with premium amenities and extended range.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Capacity</span>
                      <span className="font-medium">6-12 passengers</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Range</span>
                      <span className="font-medium">3-4 hour flights</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Price Range</span>
                      <span className="font-medium">10,000-16,000 €/h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Per Minute</span>
                      <span className="font-medium">60 €/min</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="mb-16">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopterheader/20250417_0957_Black%20Helicopter%20in%20Asia_simple_compose_01js0trr7afhh8aabf7wm4sz2m.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyaGVhZGVyLzIwMjUwNDE3XzA5NTdfQmxhY2sgSGVsaWNvcHRlciBpbiBBc2lhX3NpbXBsZV9jb21wb3NlXzAxanMwdHJyN2FmaGg4YWFiZjd3bTRzejJtLnBuZyIsImlhdCI6MTc0NDg2MzQxNywiZXhwIjoxNzc2Mzk5NDE3fQ.rgv0cUgE1phhB2jZmbIhmOTEq80VnHTp7rtzJhRIRxE" 
                alt="Helicopter Charter" 
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Request Form */}
          <div id="request-form" className="mb-16 scroll-mt-32">
            <div className="bg-gray-50 rounded-3xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <h2 className="text-3xl font-bold mb-4">Request a Quote</h2>
                  <p className="text-base text-gray-600 mb-8">
                    Contact us for a customized quote for your helicopter charter needs. Our team will respond within 2 hours during business hours.
                  </p>
                  
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Departure Location
                        </label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Destination
                        </label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Departure Date
                        </label>
                        <input
                          type="date"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Number of Passengers
                        </label>
                        <select
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        >
                          <option value="">Select</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Information
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none"
                        placeholder="Please provide any additional details or special requests..."
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                    >
                      Send Request
                    </button>
                  </form>
                </div>
                
                <div className="hidden lg:block relative">
                  <img 
                    src="https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopterheader/20250417_0957_Black%20Helicopter%20in%20Asia_simple_compose_01js0trr7afhh8aabf7wm4sz2m.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyaGVhZGVyLzIwMjUwNDE3XzA5NTdfQmxhY2sgSGVsaWNvcHRlciBpbiBBc2lhX3NpbXBsZV9jb21wb3NlXzAxanMwdHJyN2FmaGg4YWFiZjd3bTRzejJtLnBuZyIsImlhdCI6MTc0NDg2MzQxNywiZXhwIjoxNzc2Mzk5NDE3fQ.rgv0cUgE1phhB2jZmbIhmOTEq80VnHTp7rtzJhRIRxE" 
                    alt="Helicopter Interior" 
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div id="faq" className="mb-16 scroll-mt-32">
            <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4 max-w-4xl mx-auto">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer">
                    <h3 className="font-medium">What is the range of your helicopters?</h3>
                    <span className="transition-transform group-open:rotate-180">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </summary>
                  <div className="p-4 pt-0 text-base text-gray-600">
                    Our helicopter fleet has varying ranges depending on the model. Light helicopters typically have a range of 1-2 hours, medium helicopters can fly for 2-3 hours, and our VIP helicopters have extended ranges of 3-4 hours. These ranges can be affected by factors such as weather conditions and passenger load.
                  </div>
                </details>
              </div>
              
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer">
                    <h3 className="font-medium">What are the luggage restrictions?</h3>
                    <span className="transition-transform group-open:rotate-180">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </summary>
                  <div className="p-4 pt-0 text-base text-gray-600">
                    Luggage capacity varies by helicopter model. As a general guide, each passenger can bring one medium-sized soft bag. We recommend discussing your specific luggage requirements when booking to ensure we can accommodate your needs.
                  </div>
                </details>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer">
                    <h3 className="font-medium">Can helicopters fly in bad weather?</h3>
                    <span className="transition-transform group-open:rotate-180">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </summary>
                  <div className="p-4 pt-0 text-base text-gray-600">
                    Helicopters operate under Visual Flight Rules (VFR), which means they require good visibility to fly safely. Poor weather conditions such as heavy rain, snow, fog, or strong winds may cause delays or cancellations. Safety is our top priority, and our pilots make the final decision based on current and forecasted weather conditions.
                  </div>
                </details>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer">
                    <h3 className="font-medium">What is the carbon footprint of helicopter travel?</h3>
                    <span className="transition-transform group-open:rotate-180">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </summary>
                  <div className="p-4 pt-0 text-base text-gray-600">
                    We're committed to environmental responsibility. Each flight's carbon footprint is automatically calculated, and we purchase carbon offset credits from verified environmental projects. These offsets are recorded on the blockchain for complete transparency, and you'll receive a certificate detailing your contribution to carbon reduction.
                  </div>
                </details>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-black text-white p-12 rounded-2xl text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Experience Helicopter Travel?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Contact us today to book your helicopter charter or learn more about our services.
            </p>
            <a 
              href="#request-form" 
              className="inline-block bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}